import React, { useState } from 'react';
import ReportShell from './ReportShell';
import useReport from './useReport';
import FunnelBars from './charts/FunnelBars';
import DivergingBars from './charts/DivergingBars';

/** null means "no denominator", which is NOT 0%. See the backend's
 *  paywall_funnel(): rendering an em dash keeps "nobody has seen this gate"
 *  distinguishable from "this gate converts nobody". */
const rate = (n) => (n === null || n === undefined ? '—' : `${Math.round(n * 100)}%`);

/**
 * Human labels for the era keys. `unrecorded` is a real state, not an error:
 * analytics_daily_flags has one row per day written by the rollup cron, and a
 * day it did not run cannot be assigned to either era.
 */
const ERA_LABELS = {
  paywall_live: 'Paywall live (PAYMENTS_REQUIRED true)',
  pre_paywall: 'Before the paywall (no gate in front of anyone)',
  unrecorded: 'Era unrecorded — the daily flag is missing for these days',
};
// Post first: it is the era anyone acting on this report is in.
const ERA_ORDER = ['paywall_live', 'pre_paywall', 'unrecorded'];

function PaywallEra({ eraKey, rows, inert }) {
  return (
    <div key={eraKey}>
      <h3>{ERA_LABELS[eraKey] || eraKey}</h3>
      <div className="report-table-wrap">
        <table className="report-table">
          <thead>
            <tr>
              <th>Gate</th><th>Saw it</th><th>Impressions</th><th>Chose a plan</th>
              <th>Converted</th><th>Dismissed</th><th>Saw → chose</th><th>Chose → paid</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.trigger}>
                <td>
                  {r.trigger}
                  {inert[r.trigger] && (
                    <span className="report-scorecard-dash" title={inert[r.trigger]}> (inert)</span>
                  )}
                  {r.thin && !inert[r.trigger] && (
                    <span className="report-thin-flag" title="Small sample"> ·</span>
                  )}
                </td>
                <td>{r.shown}</td>
                <td>{r.impressions}</td>
                <td>{r.selected}</td>
                <td>{r.converted}</td>
                <td>{r.dismissed}</td>
                <td>{rate(r.shown_to_selected)}</td>
                <td>{rate(r.selected_to_converted)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * R4 - Conversion & Drop-off.
 *
 * Three questions on one page: which gate sells, where signups die before they
 * ever reach a gate, and what converters do differently.
 *
 * The eras are rendered as SEPARATE TABLES with no total. Everyone who
 * converted before PAYMENTS_REQUIRED went true on 2026-08-25 did so with no
 * gate in front of them; a combined rate would be a confident wrong number,
 * and a combined rate is exactly what a single table invites someone to
 * compute by eye.
 */
export default function ConversionReport() {
  const [population, setPopulation] = useState('organic');
  const { data, loading, error } = useReport('conversion', population, '&days=90');
  const [expanded, setExpanded] = useState(null);

  const eras = data?.paywall?.eras || {};
  const inert = data?.paywall?.inert_triggers || {};
  const presentEras = ERA_ORDER.filter((k) => eras[k]);

  return (
    <ReportShell
      title="Conversion & Drop-off"
      headline={data?.headline}
      population={population}
      onPopulationChange={setPopulation}
      populationSize={data?.population_size ?? 0}
      thin={data?.thin}
      thinLabel="No paywall impressions yet — these rows are structure, not results."
      loading={loading}
      error={error}
    >
      {data?.era && (
        <p className="report-note">
          <strong>Two eras, never added together.</strong> {data.era.note}
          {data.era.flip_day && ` Flip recorded ${data.era.flip_day}.`}
          {' '}In this window: {data.era.days_post} day(s) with the paywall live,
          {' '}{data.era.days_pre} before it
          {data.era.days_unrecorded > 0 && `, ${data.era.days_unrecorded} with no flag recorded`}.
        </p>
      )}

      <h3>Paywall funnel</h3>
      <p className="report-note">
        Shown as a table on purpose: the two eras must not be read against
        each other, and a chart placing them side by side invites exactly
        that.
      </p>
      {presentEras.length === 0 && (
        <p className="report-note">No paywall events in this window.</p>
      )}
      {presentEras.map((k) => (
        <PaywallEra key={k} eraKey={k} rows={eras[k]} inert={inert} />
      ))}
      {Object.keys(inert).length > 0 && (
        <ul className="report-gaps">
          {Object.entries(inert).map(([trigger, reason]) => (
            <li key={trigger}><strong>{trigger}:</strong> {reason}</li>
          ))}
        </ul>
      )}

      <h3>Activation</h3>
      <FunnelBars
        stages={(data?.activation?.stages || []).map((s) => ({
          key: s.key,
          label: s.label,
          count: s.count,
          pctFromPrev: s.pct_from_prev,
        }))}
      />
      <div className="report-table-wrap">
        <table className="report-table">
          <thead>
            <tr><th>Stage</th><th>Reached</th><th>From previous</th><th>Stuck here</th></tr>
          </thead>
          <tbody>
            {(data?.activation?.stages || []).map((s) => (
              <tr key={s.key}>
                <td>{s.label}</td>
                <td>{s.count}</td>
                <td>{s.pct_from_prev === undefined ? '—' : rate(s.pct_from_prev)}</td>
                <td>
                  {s.stuck_count ? (
                    <button
                      type="button"
                      className="report-link-button"
                      onClick={() => setExpanded(expanded === s.key ? null : s.key)}
                    >
                      {s.stuck_count} {expanded === s.key ? '▾' : '▸'}
                    </button>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {expanded && (data?.activation?.stuck?.[expanded] || []).length > 0 && (
        <>
          <ul className="report-gaps">
            {data.activation.stuck[expanded].map((u) => (
              <li key={u.user_id}>
                <a href={`/admin/users?user=${u.user_id}`}>{u.name}</a> — {u.email}
              </li>
            ))}
          </ul>
          {/* The list is capped server-side. Rendering it without its true
              size is how a truncated list gets read as the complete set of
              people to call. */}
          <p className="report-note">
            Showing {data.activation.stuck[expanded].length} of{' '}
            {(data.activation.stages.find((s) => s.key === expanded) || {}).stuck_count}.
          </p>
        </>
      )}

      <h3>What converters do differently</h3>
      <DivergingBars
        rows={(data?.behaviour_diff || []).map((r) => ({
          key: r.screen,
          label: r.screen,
          value: r.difference,
        }))}
      />
      <div className="report-table-wrap">
        <table className="report-table">
          <thead>
            <tr>
              <th>Screen</th><th>Converters</th><th>Non-converters</th><th>Difference</th>
            </tr>
          </thead>
          <tbody>
            {(data?.behaviour_diff || []).map((d) => (
              <tr key={d.screen}>
                <td>{d.screen}</td>
                <td>{rate(d.converter_rate)} ({d.converter_users})</td>
                <td>{rate(d.non_converter_rate)} ({d.non_converter_users})</td>
                <td>{d.difference > 0 ? '+' : ''}{rate(d.difference)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data?.behaviour_diff_note && (
        <p className="report-note">{data.behaviour_diff_note}</p>
      )}
    </ReportShell>
  );
}
